import {useEffect, useId, useState} from "react";
import {
  Box,
  Button,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Icon,
  IconButton,
  Skeleton,
  SkeletonCircle,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {BsChevronRight, BsLightningChargeFill} from "react-icons/bs";
import type {ICreditsBalance} from "../../models/interfaces/credits.interface.ts";
import {useCreditsStore} from "../../store/useCreditsStore.ts";
import useUserStore from "../../store/useUserStore.ts";
import {DEFAULT_CREDIT_RATES} from "../../utils/creditsCost.ts";
import PlanUsageMeter from "./PlanUsageMeter.tsx";
import {
  compactCount,
  creditsLeft,
  getCta,
  getLegend,
  getPlanHeader,
  getRingSummary,
  getRingTone,
  isShort,
  readCredits,
} from "./creditsWidgetState.ts";
import {
  METER_COLORS,
  RING_SIZE,
  RING_THICKNESS,
  blockWash,
  ctaHitArea,
  filledCtaStyle,
  focusRing,
  pressTransition,
  ringPress,
  tabularNums,
} from "./creditsWidgetStyles.ts";


type CreditsStatus = ReturnType<typeof useCreditsStore.getState>["status"];

interface CreditsUsageWidgetProps {
  balance: ICreditsBalance | null;
  status: CreditsStatus;
  minimized: boolean;
  onUpgrade: () => void;
  onManageBilling: () => void;
}

const REFRESH_INTERVAL_MS = 60_000;
const CreditsUsageWidget = ({balance, status, minimized, onUpgrade, onManageBilling}: CreditsUsageWidgetProps) => {
  const workspaceId = useUserStore((state) => state.user?.workspaceId);
  const fetchBalance = useCreditsStore((state) => state.fetchBalance);
  const rates = useCreditsStore((state) => state.catalog?.rates) ?? DEFAULT_CREDIT_RATES;
  const packages = useCreditsStore((state) => state.catalog?.packages) ?? [];
  const headerTextId = useId();
  const [hasSettled, setHasSettled] = useState(false);
  if (!hasSettled && (status === "loaded" || status === "error")) setHasSettled(true);

  useEffect(() => {
    if (!workspaceId) return;
    const interval = setInterval(() => {
      void fetchBalance(workspaceId);
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [workspaceId, fetchBalance]);

  // Loading, or settled without a balance: nothing is known about the plan, so no action is offered.
  if (!balance) {
    if (minimized) {
      return (
        <VStack spacing={2} data-testid="credits-usage">
          {hasSettled ? (
            <Tooltip label="Credits unavailable" placement="right" hasArrow>
              <CircularProgress
                value={0}
                trackColor="n.600"
                size={RING_SIZE}
                thickness={RING_THICKNESS}
                aria-label="Credits unavailable"
              />
            </Tooltip>
          ) : (
            <SkeletonCircle size={RING_SIZE}/>
          )}
        </VStack>
      );
    }
    return (
      <Box w="full" data-testid="credits-usage">
        {hasSettled ? (
          <Text fontSize="sm" color="n.300">Credits unavailable</Text>
        ) : (
          <VStack align="stretch" spacing={2}>
            <Skeleton height="1rem" rounded="md"/>
            <Skeleton height="0.5rem" rounded="full"/>
          </VStack>
        )}
      </Box>
    );
  }

  const reading = readCredits(balance, rates);
  const {state, planUsed} = reading;
  const header = getPlanHeader(reading);
  const cta = getCta(reading, packages);
  const onCta = cta?.opens === "billing" ? onManageBilling : onUpgrade;
  const short = isShort(reading);
  // Unlimited shows no header text but its block still opens billing.
  const opensBilling = header !== null || state === "unlimited";
  const headline = balance.unlimited ? "Unlimited credits" : creditsLeft(balance.balance);

  if (minimized) {
    const summary = getRingSummary(reading, header);
    return (
      <VStack spacing={2} data-testid="credits-usage">
        <Tooltip label={summary} placement="right" hasArrow>
          <Box position="relative" display="flex" rounded="full" {...ringPress}>
            <CircularProgress
              // With no plan to measure, a full ring frames the count instead of implying a share.
              value={planUsed ?? 100}
              valueText={summary}
              color={METER_COLORS[getRingTone(reading)]}
              trackColor="n.600"
              size={RING_SIZE}
              thickness={RING_THICKNESS}
              aria-label="Credits left"
            >
              <CircularProgressLabel fontSize="xs" fontWeight="semibold" color="n.100" sx={tabularNums}>
                {balance.unlimited ? "∞" : compactCount.format(Math.max(0, balance.balance))}
              </CircularProgressLabel>
            </CircularProgress>
            <Box
              as="button"
              type="button"
              aria-label="Manage plan and billing"
              onClick={onManageBilling}
              position="absolute"
              inset={0}
              rounded="full"
              _focusVisible={focusRing}
            />
          </Box>
        </Tooltip>
        {cta && (
          <Tooltip label={cta.label} placement="right" hasArrow>
            <IconButton
              aria-label={cta.label}
              icon={<BsLightningChargeFill/>}
              {...filledCtaStyle}
              size="sm"
              isRound
              _before={{content: '""', position: "absolute", inset: "-4px", rounded: "full"}}
              onClick={onCta}
              data-testid="credits-upgrade"
            />
          </Tooltip>
        )}
      </VStack>
    );
  }

  const legend = getLegend(reading);
  return (
    <Box w="full" data-testid="credits-usage">
      <Box
        position="relative"
        zIndex={0}
        mx={-3}
        px={3}
        py={2.5}
        rounded="xl"
        data-group={opensBilling ? "" : undefined}
        {...(opensBilling ? blockWash : {})}
      >
        {opensBilling && (
          <Flex
            as="button"
            type="button"
            aria-label="Manage plan and billing"
            aria-describedby={header ? headerTextId : undefined}
            onClick={onManageBilling}
            w="full"
            mb={header ? 1 : 0}
            align="center"
            justify="space-between"
            gap={2}
            textAlign="left"
            fontSize="xs"
            // Stretched over the whole block, so it is one click target while the meter stays outside the button.
            _before={{content: '""', position: "absolute", inset: 0, zIndex: 1, rounded: "xl"}}
            _focusVisible={{outline: "none", _before: focusRing}}
          >
            {header && (
              <>
                <Text as="span" id={headerTextId} color="n.300">
                  <Text as="span" fontWeight="semibold" color="n.100">{header.name}</Text>
                  {header.detail && (
                    <>
                      {" · "}
                      <Text as="span" color={header.isWarning ? "sunsetAmber.300" : undefined}>{header.detail}</Text>
                    </>
                  )}
                </Text>
                <Icon as={BsChevronRight} aria-hidden="true" boxSize={3} flexShrink={0} color="n.400"/>
              </>
            )}
          </Flex>
        )}
        <Text
          className="text-balance"
          fontSize="lg"
          fontWeight="semibold"
          lineHeight="short"
          color={short ? "sunsetAmber.300" : "n.100"}
          sx={tabularNums}
        >
          {headline}
        </Text>
        {planUsed !== null && <PlanUsageMeter usedPercent={planUsed} isWarning={short}/>}
        {legend.length > 0 && (
          <VStack mt={2} align="stretch" spacing={1} fontSize="xs" color="n.300" sx={tabularNums}>
            {legend.map((line) => <Text key={line}>{line}</Text>)}
          </VStack>
        )}
      </Box>
      {cta && (
        <Button
          mt={2}
          w="full"
          size="sm"
          rounded="lg"
          {...filledCtaStyle}
          leftIcon={<BsLightningChargeFill/>}
          _before={ctaHitArea}
          onClick={onCta}
          data-testid="credits-upgrade"
        >
          {cta.label}
        </Button>
      )}
    </Box>
  );
};

export default CreditsUsageWidget;
